/**
 * Primary action buttons for the floating toolbar.
 *
 * Renders undo/redo, add, code, run/chat, publish, save, and assistant buttons
 * with visual hierarchy: Add (primary fill), Run (outline), rest (subtle).
 */

import React, { useCallback } from "react";
import {
  Tooltip,
  Badge,
  mergeClasses,
} from "@coreai/fluentui-react";
import {
  Add20Regular,
  Add20Filled,
  Code20Regular,
  Code20Filled,
  ChatAdd20Regular,
  Play20Regular,
  ArrowUndo20Regular,
  ArrowRedo20Regular,
} from "@fluentui/react-icons";
import { IconCrossfade } from "./IconCrossfade";
import { CopilotLogoIcon } from "@/components/common/CopilotIcon";
import { useSessionHistoryStore } from "@/stores/sessionHistoryStore";
import { useAgentChatAguiStore } from "@/stores/agentChatAguiStore";
import { telemetryService } from "@/services/telemetryService";
import { useStyles } from "./dockToolbar.styles";
import { useDraftModeOptional } from "@/contexts";
import { getShortcutLabel } from "@/utils/input";

// Pre-format toolbar shortcut hints once at module load (IS_MAC is
// constant for the lifetime of the page).
const SHORTCUT_UNDO = getShortcutLabel('undo');
const SHORTCUT_REDO = getShortcutLabel('redo');
const SHORTCUT_ADD = getShortcutLabel('addOperation');
const SHORTCUT_CODE = getShortcutLabel('toggleCode');
const SHORTCUT_RUN = getShortcutLabel('run');

export interface PrimaryActionsProps {
  isPanelExpanded: boolean;
  onTogglePanel: () => void;
  isCodeVisible: boolean;
  onToggleCode: () => void;
  isConversational?: boolean;
  onRun?: () => void;
  onStartChat?: () => void;
  isChatOpen: boolean;
  onToggleChat: () => void;
  disableCommands?: boolean;
}

export const PrimaryActions: React.FC<PrimaryActionsProps> = ({
  isPanelExpanded,
  onTogglePanel,
  isCodeVisible,
  onToggleCode,
  isConversational = false,
  onRun,
  onStartChat,
  isChatOpen,
  onToggleChat,
  disableCommands = false,
}) => {
  const styles = useStyles();
  const canUndo = useSessionHistoryStore((state) => state.canUndo());
  const canRedo = useSessionHistoryStore((state) => state.canRedo());
  // Surface a badge on the Copilot button when the assistant produced results
  // while it was closed (there is no longer a persistent rail to show this).
  const hasUnreadChat = useAgentChatAguiStore((state) => state.hasUnreadMessages);
  const showChatUnread = hasUnreadChat && !isChatOpen;
  const undo = useSessionHistoryStore((state) => state.undo);
  const redo = useSessionHistoryStore((state) => state.redo);
  const draftMode = useDraftModeOptional();
  // Block all workflow-mutating actions while viewing the published version.
  const isReadOnly = draftMode?.isReadOnly ?? false;
  const isDraftMode = draftMode?.isDraftMode ?? false;
  const runLabel = isDraftMode ? "Test" : "Run";
  const runAriaLabel = isDraftMode ? "Test" : "Run workflow";
  const runTooltipLabel = isDraftMode ? "Test draft workflow" : "Run workflow";

  const handleUndo = useCallback(() => {
    telemetryService.trackEvent("workflow.undo");
    undo();
  }, [undo]);

  const handleRedo = useCallback(() => {
    telemetryService.trackEvent("workflow.redo");
    redo();
  }, [redo]);

  return (
    <>
      {/* Undo / Redo (labeled, low-emphasis) */}
      <Tooltip content={isReadOnly ? "Switch to draft mode to edit" : `Undo (${SHORTCUT_UNDO})`} relationship="label">
        <button
          type="button"
          className={styles.centerButton}
          onClick={handleUndo}
          disabled={!canUndo || disableCommands || isReadOnly}
          aria-label="Undo"
        >
          <span className={styles.centerButtonIcon}>
            <ArrowUndo20Regular aria-hidden="true" />
          </span>
          <span className={styles.centerButtonLabel}>Undo</span>
        </button>
      </Tooltip>
      <Tooltip content={isReadOnly ? "Switch to draft mode to edit" : `Redo (${SHORTCUT_REDO})`} relationship="label">
        <button
          type="button"
          className={styles.centerButton}
          onClick={handleRedo}
          disabled={!canRedo || disableCommands || isReadOnly}
          aria-label="Redo"
        >
          <span className={styles.centerButtonIcon}>
            <ArrowRedo20Regular aria-hidden="true" />
          </span>
          <span className={styles.centerButtonLabel}>Redo</span>
        </button>
      </Tooltip>

      <div className={styles.islandDivider} />

      {/* Add Button (primary visual weight) */}
      <Tooltip content={isReadOnly ? "Switch to draft mode to edit" : `Add operation (${SHORTCUT_ADD})`} relationship="label">
        <button
          type="button"
          className={mergeClasses(
            styles.centerButton,
            styles.centerButtonPrimary,
            isPanelExpanded && styles.centerButtonActive,
          )}
          onClick={onTogglePanel}
          disabled={disableCommands || isReadOnly}
          aria-label="Add operation"
          aria-expanded={isPanelExpanded}
        >
          <span className={styles.centerButtonIcon}>
            <IconCrossfade
              isActive={isPanelExpanded}
              activeIcon={<Add20Filled />}
              inactiveIcon={<Add20Regular />}
            />
          </span>
          <span className={styles.centerButtonLabel}>Add</span>
        </button>
      </Tooltip>

      {/* Code Button (subtle) */}
      <Tooltip
        content={isCodeVisible ? `Hide code (${SHORTCUT_CODE})` : `Show code (${SHORTCUT_CODE})`}
        relationship="label"
      >
        <button
          type="button"
          className={mergeClasses(
            styles.centerButton,
            isCodeVisible && styles.centerButtonActive,
          )}
          onClick={onToggleCode}
          disabled={disableCommands}
          aria-label={isCodeVisible ? "Hide code" : "Show code"}
          aria-pressed={isCodeVisible}
        >
          <span className={styles.centerButtonIcon}>
            <IconCrossfade
              isActive={isCodeVisible}
              activeIcon={<Code20Filled />}
              inactiveIcon={<Code20Regular />}
            />
          </span>
          <span className={styles.centerButtonLabel}>Code</span>
        </button>
      </Tooltip>

      {/* Run / Chat Button — mode-aware for procedural workflows */}
      {isConversational && onStartChat ? (
        <Tooltip content="Start chat" relationship="label">
          <button
            type="button"
            className={styles.centerButton}
            onClick={onStartChat}
            disabled={disableCommands}
            aria-label="Start chat"
          >
            <span className={styles.centerButtonIcon}>
              <ChatAdd20Regular aria-hidden="true" />
            </span>
            <span className={styles.centerButtonLabel}>Chat</span>
          </button>
        </Tooltip>
      ) : (
        onRun && (
          <Tooltip content={`${runTooltipLabel} (${SHORTCUT_RUN})`} relationship="description">
            <button
              type="button"
              className={styles.centerButton}
              onClick={onRun}
              disabled={disableCommands}
              aria-label={runAriaLabel}
            >
              <span className={styles.centerButtonIcon}>
                <Play20Regular aria-hidden="true" />
              </span>
              <span className={styles.centerButtonLabel}>{runLabel}</span>
            </button>
          </Tooltip>
        )
      )}

      <div className={styles.islandDivider} />

      {/* Copilot Chat Button (subtle) */}
      <Tooltip
        content={isChatOpen ? "Hide Copilot (Experimental)" : "Show Copilot (Experimental)"}
        relationship="label"
      >
        <button
          type="button"
          className={mergeClasses(
            styles.centerButton,
            isChatOpen && styles.centerButtonActive,
          )}
          onClick={onToggleChat}
          disabled={disableCommands}
          aria-label={
            showChatUnread
              ? "Show Copilot (Experimental) — new results"
              : isChatOpen
                ? "Hide Copilot (Experimental)"
                : "Show Copilot (Experimental)"
          }
          aria-pressed={isChatOpen}
        >
          <span className={styles.centerButtonIcon}>
            <IconCrossfade
              isActive={isChatOpen}
              activeIcon={<CopilotLogoIcon fontSize={20} />}
              inactiveIcon={<CopilotLogoIcon fontSize={20} />}
            />
            {showChatUnread && (
              <Badge
                className={styles.copilotUnreadBadge}
                size="tiny"
                color="danger"
                aria-hidden="true"
              />
            )}
          </span>
          <span className={styles.centerButtonLabel}>Copilot</span>
        </button>
      </Tooltip>
    </>
  );
};
